// Conversation hook for the SQ-AI page.
//
// Modeled on chart-agent's useConversation but trimmed to chart-mode output:
// no thinking pane, tool pills, or clarifications — each assistant turn is
// just assumptions + charts + an optional caption, with a single status line
// while the agent works.
//
// Turn routing matches the dev UI: the first message opens the conversation
// via ai:chart_query (the server locks mode:"chart" + provider to the
// conversation at that point — socket.js mode stickiness), and every later
// message is a follow-up via ai:user_message on the SAME conversationId, so
// the agent keeps context across turns ("now show it by month").

import { useCallback, useEffect, useReducer, useRef } from "react";

const initial = (id) => ({ id, messages: [], status: "idle" }); // idle | in_flight

function updateLastAssistant(state, updater) {
  const messages = [...state.messages];
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") return state;
  messages[messages.length - 1] = updater(last);
  return { ...state, messages };
}

// Freeze any still-"live" thinking step → "done" (so it stops animating when the
// turn settles, even if no explicit thinking 'done' event arrived).
function freezeThinking(steps) {
  return (steps || []).map((s) => (s.status === "live" ? { ...s, status: "done" } : s));
}

function reducer(state, action) {
  switch (action.type) {
    case "reset":
      return initial(action.id);

    case "send":
      // Append the user turn and an assistant placeholder in one step so
      // every subsequent event lands on a live assistant message.
      return {
        ...state,
        status: "in_flight",
        messages: [
          ...state.messages,
          { id: `u-${action.ts}`, role: "user", text: action.text },
          {
            id: `a-${action.ts}`,
            role: "assistant",
            assumptions: [],
            charts: [],
            caption: "",
            done: false,
            error: null,
            durationMs: null,
            sqls: [], // dev-only: generated SQL per query tool call (Fix 3 accordion)
            thinkingSteps: [], // streaming "Thinking…" phase/update lines (ai:thinking)
            thinkingTick: 0, // bumps on every thinking event so the UI gap-clock resets reliably
            suggestions: null, // starter cards from a greeting short-circuit (ai:done.suggestions)
            resultMeta: null, // #2a: window / date basis / modules caption (ai:done.resultMeta)
          },
        ],
      };

    case "assumption":
      return updateLastAssistant(state, (m) => ({
        ...m,
        assumptions: [...m.assumptions, { text: action.text, rationale: action.rationale || null }],
      }));

    case "sql":
      // Dev-only: the agent surfaces generated SQL on ai:tool_call (uiMode dev).
      // De-dupe by tool_use_id so a started/completed pair doesn't double-list.
      return updateLastAssistant(state, (m) => {
        const sqls = m.sqls || [];
        if (sqls.some((s) => s.id === action.id)) return m;
        return { ...m, sqls: [...sqls, { id: action.id, name: action.name, sql: action.sql }] };
      });

    case "chart":
      return updateLastAssistant(state, (m) => ({ ...m, charts: [...m.charts, action.spec] }));

    case "caption":
      return updateLastAssistant(state, (m) => ({ ...m, caption: (m.caption || "") + action.text }));

    case "thinking_phase":
      // New real step. Freeze the current live line, then push a new live one.
      return updateLastAssistant(state, (m) => {
        if (m.done) return m;
        const frozen = freezeThinking(m.thinkingSteps);
        return {
          ...m,
          thinkingTick: (m.thinkingTick || 0) + 1,
          thinkingSteps: [
            ...frozen,
            { id: `t-${action.ts}-${frozen.length}`, icon: action.icon || null, phrase: action.phrase || "", status: "live" },
          ],
        };
      });

    case "thinking_update":
      // Sub-progress within the current step — replace the active line in place.
      return updateLastAssistant(state, (m) => {
        const steps = m.thinkingSteps || [];
        let i = -1;
        for (let k = steps.length - 1; k >= 0; k--) {
          if (steps[k].status === "live") { i = k; break; }
        }
        if (i < 0) return m;
        const next = steps.slice();
        next[i] = { ...next[i], phrase: action.phrase || next[i].phrase };
        // Bump the tick even when the phrase is byte-identical, so the UI's
        // gap-fill clock resets on every real event (an update IS an event).
        return { ...m, thinkingSteps: next, thinkingTick: (m.thinkingTick || 0) + 1 };
      });

    case "thinking_done":
      return updateLastAssistant(state, (m) => ({
        ...m,
        thinkingSteps: freezeThinking(m.thinkingSteps),
        thinkingTick: (m.thinkingTick || 0) + 1,
      }));

    case "done":
      return {
        ...updateLastAssistant(state, (m) => ({
          ...m,
          done: true,
          durationMs: action.durationMs || null,
          suggestions: Array.isArray(action.suggestions) && action.suggestions.length ? action.suggestions : null,
          resultMeta: action.resultMeta || null,
          thinkingSteps: freezeThinking(m.thinkingSteps),
        })),
        status: "idle",
      };

    case "error":
      return {
        ...updateLastAssistant(state, (m) => ({
          ...m,
          done: true,
          error: { code: action.code, message: action.message },
          thinkingSteps: freezeThinking(m.thinkingSteps),
        })),
        status: "idle",
      };

    default:
      return state;
  }
}

export function useSqAiConversation({ socket, conversationId, tenantSchema, context }) {
  const [state, dispatch] = useReducer(reducer, conversationId, initial);

  // The picker's context object rides every turn. Kept in a ref so `send` stays
  // stable (and always reads the latest pinned scope) without re-subscribing.
  const contextRef = useRef(context);
  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  useEffect(() => {
    dispatch({ type: "reset", id: conversationId });
  }, [conversationId]);

  useEffect(() => {
    if (!socket) return;
    const matches = (p) => p && p.conversationId === conversationId;

    const handlers = {
      "ai:assumption":    (p) => matches(p) && dispatch({ type: "assumption", text: p.assumption, rationale: p.rationale }),
      "ai:chart":         (p) => matches(p) && dispatch({ type: "chart", spec: p.spec }),
      // ai:stage / ai:progress_text are intentionally not handled: the streaming
      // "Thinking…" status (ai:thinking) supersedes the old single status line.
      "ai:text_delta":    (p) => matches(p) && dispatch({ type: "caption", text: p.text || "" }),
      // Streaming "Thinking…" status (thinking_status_spec.md). The phrase is the
      // backend's user-facing text; we never derive it from tool names here.
      "ai:thinking":      (p) => {
        if (!matches(p)) return;
        if (p.type === "phase") dispatch({ type: "thinking_phase", icon: p.icon, phrase: p.phrase, ts: Date.now() });
        else if (p.type === "update") dispatch({ type: "thinking_update", phrase: p.phrase });
        else if (p.type === "done") dispatch({ type: "thinking_done" });
      },
      // Dev-only: the agent forwards generated SQL on ai:tool_call when uiMode
      // is "dev" (production never receives it). Collect it for the SQL accordion.
      "ai:tool_call":     (p) => matches(p) && p.executedSql && dispatch({ type: "sql", id: p.tool_use_id, name: p.name, sql: p.executedSql }),
      // The chart-mode prompt tells the model to write clarifications as prose,
      // but if it still calls ask_clarifying_question (older transcripts have
      // shown this) the structured payload would never render here without
      // this listener — caption stays empty, the turn looks like a dead-end.
      // Promote the question (plus any 2–4 options the model attached) into the
      // turn's caption so a clarifying ask always shows in the thread.
      "ai:clarification": (p) => {
        if (!matches(p)) return;
        const lines = [];
        if (p.question) lines.push(p.question);
        if (Array.isArray(p.options) && p.options.length > 0) {
          for (const opt of p.options) {
            const label = typeof opt === "string" ? opt : (opt && (opt.label || opt.value));
            if (label) lines.push(`- ${label}`);
          }
        }
        const text = lines.join("\n");
        if (text) dispatch({ type: "caption", text });
      },
      "ai:done":          (p) => matches(p) && dispatch({ type: "done", durationMs: p.durationMs, suggestions: p.suggestions, resultMeta: p.resultMeta }),
      "ai:error":         (p) => matches(p) && dispatch({ type: "error", code: p.code, message: p.message }),
    };

    for (const [event, handler] of Object.entries(handlers)) socket.on(event, handler);
    return () => {
      for (const [event, handler] of Object.entries(handlers)) socket.off(event, handler);
    };
  }, [socket, conversationId]);

  const send = useCallback(
    (text) => {
      if (!socket || !text || !tenantSchema) return;
      const isFirst = state.messages.length === 0;
      // Per chip_context_wiring_spec.md: send the pinned scope alongside every
      // turn. The backend doesn't consume it yet (the picker is inert until the
      // matcher/conflict step lands) — extra payload fields are ignored today.
      const context = contextRef.current || undefined;
      dispatch({ type: "send", text, ts: Date.now() });
      if (isFirst) {
        socket.emit("ai:chart_query", {
          conversationId,
          queryText: text,
          tenantSchema,
          provider: "llm-agent",
          mode: "chart",
          context,
        });
      } else {
        socket.emit("ai:user_message", { conversationId, text, tenantSchema, provider: "llm-agent", context });
      }
    },
    [socket, conversationId, tenantSchema, state.messages.length],
  );

  const cancel = useCallback(() => {
    if (!socket) return;
    socket.emit("ai:cancel", { conversationId });
  }, [socket, conversationId]);

  return { state, send, cancel };
}
